import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, 
  MessageSquare, 
  TrendingUp,
  TrendingDown,
  Minus,
  Users,
  FileText,
  Map,
  ChevronRight,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const levelColors = {
  federal: 'bg-red-500',
  state: 'bg-blue-500',
  local: 'bg-green-500'
};

const sentimentIcons = {
  positive: TrendingUp,
  neutral: Minus,
  negative: TrendingDown
};

const sentimentColors = {
  positive: 'text-green-600',
  neutral: 'text-gray-600',
  negative: 'text-red-600'
};

export default function FeedbackDashboard({ user }) {
  const [selectedLevel, setSelectedLevel] = useState('federal');
  const [feedbackData, setFeedbackData] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [issueDetails, setIssueDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeedbackSummary(selectedLevel);
  }, [selectedLevel]);

  useEffect(() => {
    if (selectedIssue) {
      fetchIssueDetails(selectedIssue);
    }
  }, [selectedIssue]);

  const fetchFeedbackSummary = async (level) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/feedback/summary-by-level/${level}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFeedbackData(data);
        // Auto-select first issue with responses
        const firstWithData = data.issues?.find(i => i.response_count > 0);
        if (firstWithData) {
          setSelectedIssue(firstWithData.issue_id);
        } else if (data.issues?.length > 0) {
          setSelectedIssue(data.issues[0].issue_id);
        }
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIssueDetails = async (issueId) => {
    try {
      const response = await fetch(`${API}/feedback/by-issue/${issueId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setIssueDetails(data);
      }
    } catch (error) {
      console.error('Error fetching issue details:', error);
    }
  };

  const getSentimentPercentage = (sentiment, total) => {
    if (!total) return 0;
    return Math.round((sentiment / total) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="feedback-dashboard">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              </Link>
              <div className="h-6 w-px bg-border"></div>
              <h1 className="font-semibold flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Feedback Routing Dashboard
              </h1>
            </div>
            <Link to="/admin/templates">
              <Button variant="outline" size="sm">
                <FileText className="mr-2 h-4 w-4" />
                Survey Templates
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Level Tabs */}
        <Tabs value={selectedLevel} onValueChange={setSelectedLevel} className="mb-8">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="federal" className="data-[state=active]:bg-red-100 data-[state=active]:text-red-800">
              Federal
            </TabsTrigger>
            <TabsTrigger value="state" className="data-[state=active]:bg-blue-100 data-[state=active]:text-blue-800">
              State
            </TabsTrigger>
            <TabsTrigger value="local" className="data-[state=active]:bg-green-100 data-[state=active]:text-green-800">
              Local (LGA)
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="metric-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Issues Tracked</p>
                  <p className="text-3xl font-bold">{feedbackData?.total_issues || 0}</p>
                </div>
                <div className={`w-12 h-12 ${levelColors[selectedLevel]} rounded-lg flex items-center justify-center`}>
                  <AlertCircle className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="metric-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Responses</p>
                  <p className="text-3xl font-bold">{feedbackData?.total_responses || 0}</p>
                </div>
                <Users className="h-8 w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="metric-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Surveys</p>
                  <p className="text-3xl font-bold">
                    {feedbackData?.issues?.reduce((sum, i) => sum + i.survey_count, 0) || 0}
                  </p>
                </div>
                <FileText className="h-8 w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Issues List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Issues at {selectedLevel.charAt(0).toUpperCase() + selectedLevel.slice(1)} Level</CardTitle>
                <CardDescription>Click an issue to view detailed feedback</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {feedbackData?.issues?.map((issue) => (
                    <button
                      key={issue.issue_id}
                      onClick={() => setSelectedIssue(issue.issue_id)}
                      className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${
                        selectedIssue === issue.issue_id ? 'bg-muted' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{issue.issue}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-muted-foreground">
                              {issue.response_count} responses
                            </span>
                            {issue.dominant_sentiment !== 'no data' && (
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${sentimentColors[issue.dominant_sentiment]}`}
                              >
                                {issue.dominant_sentiment}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Issue Details */}
          <div className="lg:col-span-2">
            {issueDetails ? (
              <div className="space-y-6">
                {/* Issue Header */}
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{issueDetails.issue_info?.issue}</CardTitle>
                        <CardDescription className="mt-1">
                          {issueDetails.issue_info?.description}
                        </CardDescription>
                      </div>
                      <div className="flex gap-1">
                        {issueDetails.responsible_levels?.map((level) => (
                          <div 
                            key={level}
                            className={`w-3 h-3 rounded-full ${levelColors[level]}`}
                            title={level}
                          />
                        ))}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-2xl font-bold">{issueDetails.total_surveys}</p>
                        <p className="text-xs text-muted-foreground">Surveys</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-2xl font-bold">{issueDetails.total_responses}</p>
                        <p className="text-xs text-muted-foreground">Responses</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-2xl font-bold">
                          {Object.keys(issueDetails.state_breakdown || {}).length}
                        </p>
                        <p className="text-xs text-muted-foreground">States</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Sentiment Breakdown */}
                {issueDetails.total_responses > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Sentiment Analysis</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {['positive', 'neutral', 'negative'].map((sentiment) => {
                          const count = issueDetails.sentiment_summary?.[sentiment] || 0;
                          const percentage = getSentimentPercentage(count, issueDetails.total_responses);
                          const Icon = sentimentIcons[sentiment];
                          return (
                            <div key={sentiment}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <Icon className={`h-4 w-4 ${sentimentColors[sentiment]}`} />
                                  <span className="text-sm capitalize">{sentiment}</span>
                                </div>
                                <span className="text-sm font-medium">{count} ({percentage}%)</span>
                              </div>
                              <Progress 
                                value={percentage} 
                                className={`h-2 ${
                                  sentiment === 'positive' ? '[&>div]:bg-green-500' :
                                  sentiment === 'negative' ? '[&>div]:bg-red-500' : '[&>div]:bg-gray-400'
                                }`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Top Concerns */}
                {issueDetails.top_concerns?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Top Citizen Concerns</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {issueDetails.top_concerns.slice(0, 5).map((concern, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <span className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-xs font-bold text-primary">
                              {idx + 1}
                            </span>
                            <span className="flex-1 text-sm">{concern.concern}</span>
                            <Badge variant="secondary">{concern.count}</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Sample Feedback */}
                {issueDetails.sample_feedback?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Citizen Voices</CardTitle>
                      <CardDescription>Sample open-ended responses</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4 max-h-96 overflow-y-auto thin-scrollbar">
                        {issueDetails.sample_feedback.slice(0, 10).map((feedback, idx) => (
                          <div key={idx} className="p-3 bg-muted/30 rounded-lg border-l-4 border-primary">
                            <p className="text-sm italic">"{feedback.text}"</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">{feedback.state}</Badge>
                              {feedback.lga && (
                                <span className="text-xs text-muted-foreground">{feedback.lga}</span>
                              )}
                              <Badge 
                                className={`text-xs ${
                                  feedback.sentiment === 'positive' ? 'bg-green-100 text-green-800' :
                                  feedback.sentiment === 'negative' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {feedback.sentiment}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* No Data State */}
                {issueDetails.total_responses === 0 && (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No Feedback Yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Create a survey for this issue to start collecting citizen feedback.
                      </p>
                      <Link to="/admin/templates">
                        <Button>
                          <FileText className="mr-2 h-4 w-4" />
                          View Templates
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Select an Issue</h3>
                  <p className="text-muted-foreground">
                    Choose an issue from the list to view detailed feedback analysis.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
